import React, { useEffect, useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { VerticalFieldAndValueWithIcon } from '@components/ringil3/Containers/VerticalFieldAndValueWithIcon.tsx';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import RichTextEditor from '@components/RingilComponents/RichTextEditor/RichTextEditor';
import ConfirmDialog from '@components/RingilComponents/ConfirmDialog';
import SecuredArea from '@components/security/SecuredArea';
import { SecuredConfig } from '@components/security/securedDecoration';
import { SecuredFlag } from '@components/security/types';
import { EntityTypeEnum, InternalCommentOutDTO } from '@api/ringil-api.v2';
import { formatDateFromStringWithLocale } from '@utils/dateWithTz';
import useInternalComments from '@hooks/useInternalComments';
import SvgRoute from '@icons/route.svg?react';
import SvgMessage from '@icons/message.svg?react';
import SvgPencil from '@icons/pencil.svg?react';
import SvgTrash from '@icons/delete-trash.svg?react';

// Button placed in shipment-tab section headers; opens the internal-comments sidebar.
// Visibility is delegated to the supplied secured config (feature flag + READ permission).
interface HeaderButtonProps {
  onClick: () => void;
  secured?: SecuredConfig;
}

export const InternalCommentsHeaderButton: React.FC<HeaderButtonProps> = ({ onClick, secured }) => {
  const { tr } = useTranslationLgs();
  return (
    <RingilButton
      mode={'outlined-rectangle'}
      icon={<SvgMessage />}
      label={tr('InternalComments.sectionButton', 'Comments')}
      onClick={onClick}
      secured={secured}
      dataTestId={'internalComments-openButton'}
    />
  );
};

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  entityId: string;
  applicationId: string;
  // RW security flag (feature flag + RW permission). Gates the composer and per-comment edit/delete.
  editFlag: SecuredFlag;
}

// sanitize the editor HTML before rendering it (same approach as the chat message component).
const safeHtml = (html: string): string => {
  try {
    return sanitizeHtml(html, {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat(['u', 's', 'strike']),
      allowedAttributes: { ...sanitizeHtml.defaults.allowedAttributes, '*': ['style'] },
    });
  } catch (e) {
    return '';
  }
};

const InternalCommentsSidebar: React.FC<ComponentProps> = ({ visible, onHide, entityId, applicationId, editFlag }) => {
  const { tr } = useTranslationLgs();
  const {
    comments,
    loading,
    hasMore,
    loadInitial,
    loadMore,
    create,
    update,
    remove,
  } = useInternalComments(EntityTypeEnum.EXPEDITION, entityId);

  const [newContent, setNewContent] = useState('');
  const [composerKey, setComposerKey] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      cancelEdit();
      loadInitial();
    }
  }, [visible, entityId]);

  const submitNew = () => {
    if (!newContent) return;
    create(newContent, () => {
      setNewContent('');
      setComposerKey(prev => prev + 1); // remount the editor to clear it
    });
  };

  const startEdit = (comment: InternalCommentOutDTO) => {
    setEditingId(comment.id);
    setEditingContent(comment.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingContent('');
  };

  const submitEdit = () => {
    if (!editingContent || !editingId) return;
    update(editingId, editingContent, cancelEdit);
  };

  const confirmDelete = () => {
    if (!deletingId) return;
    remove(deletingId, () => setDeletingId(null));
  };

  // RW flag AND server-side author check (canEdit) must both hold to edit/delete a comment.
  const ownerActionSecured = (comment: InternalCommentOutDTO, action: string): SecuredConfig => ({
    id: `ic-${action}`,
    instance: comment.id,
    label: `${action} own internal comment`,
    description: `Button to ${action} an internal comment`,
    visible: [
      editFlag,
      // An author check, not "nothing to show": as `data` it would never reach the AI assistant.
      {
        type: 'ownership',
        label: 'author (canEdit)',
        satisfied: comment.canEdit,
        description: 'Author only: only the user who wrote the comment can change or delete it',
        byDesign: true,
      },
    ],
  });

  return (
    <RingilSidebar
      visible={visible}
      onBack={onHide}
      onContinue={onHide}
      showSaveButton={false}
      heading={tr('InternalComments.heading', 'Internal comments')}
    >
      <>
        <div className={'flex flex-col gap-4'}>
          <VerticalFieldAndValueWithIcon
            label={tr(`RingilForEntitySidebar.title${EntityTypeEnum.EXPEDITION}`, `RingilForEntitySidebar.title${EntityTypeEnum.EXPEDITION}`)}
            valueText={applicationId}
            icon={<SvgRoute />}
          />
          <HorizontalLine variant={'tertiary'} />
        </div>
        <div className={'flex flex-col gap-0.5 pt-4'}>
          {/* Composer — visible only with RW permission */}
          <SecuredArea id={'ic-composer'} label={'Add internal comment'} description={'Write a new internal comment on the shipment'} visible={editFlag}>
            <div className={'flex flex-col items-end gap-2 pb-4'}>
              <RichTextEditor key={composerKey} value={newContent} onChange={setNewContent} />
              <RingilButton
                mode={'filled'}
                label={tr('InternalComments.add', 'Add comment')}
                onClick={submitNew}
                disabled={!newContent}
                dataTestId={'internalComments-add'}
              />
            </div>
          </SecuredArea>

          {/* Comment list, newest first */}
          {comments.map(comment => (
            <div key={comment.id}
                 className={'flex flex-col items-center justify-center self-stretch bg-white pb-6 pl-4 pr-4 pt-4'}>
              <div className={'flex w-full flex-col gap-2'}>
                <div className={'flex flex-row items-center justify-between gap-2'}>
                <span className={'text-xs text-content-secondary'}>
                  {formatDateFromStringWithLocale(comment.createdDate)}
                  {comment.authorName ? `, ${comment.authorName}` : ''}
                  {comment.edited ? ` (${tr('InternalComments.edited', 'edited')})` : ''}
                </span>
                  {editingId !== comment.id && (
                    <div className={'flex flex-row items-center gap-1'}>
                      <RingilButton
                        mode={'table'}
                        icon={<SvgPencil />}
                        tooltip={tr('InternalComments.edit', 'Edit')}
                        onClick={() => startEdit(comment)}
                        secured={ownerActionSecured(comment, 'edit')}
                      />
                      <RingilButton
                        mode={'table'}
                        icon={<SvgTrash />}
                        tooltip={tr('InternalComments.delete', 'Delete')}
                        onClick={() => setDeletingId(comment.id)}
                        secured={ownerActionSecured(comment, 'delete')}
                      />
                    </div>
                  )}
                </div>

                {editingId === comment.id ? (
                  <div className={'flex flex-col items-end gap-2'}>
                    <RichTextEditor value={editingContent} onChange={setEditingContent} autoFocus />
                    <div className={'flex flex-row gap-2'}>
                      <RingilButton mode={'text-only'} label={tr('InternalComments.cancel', 'Cancel')}
                                    onClick={cancelEdit} />
                      <RingilButton mode={'filled'} label={tr('InternalComments.save', 'Save')} onClick={submitEdit}
                                    disabled={!editingContent} />
                    </div>
                  </div>
                ) : (
                  <div className={'text-sm text-content-primary [&_p]:m-0'}
                       dangerouslySetInnerHTML={{ __html: safeHtml(comment.content) }} />
                )}
              </div>
            </div>
          ))}

          {!loading && comments.length === 0 && (
            <div className={'flex flex-col items-center justify-center self-stretch bg-white p-4'}>
              <div
                className={'w-full text-center text-sm text-content-secondary'}>{tr('InternalComments.empty', 'No internal comments yet')}</div>
            </div>
          )}

          {/* Load older comments on click */}
          {hasMore && (
            <div className={'flex flex-row justify-center py-2'}>
              <RingilButton
                mode={'link'}
                loading={loading}
                label={tr('InternalComments.loadMore', 'Load next 10 comments')}
                onClick={loadMore}
                dataTestId={'internalComments-loadMore'}
              />
            </div>
          )}
        </div>

        <ConfirmDialog
          visible={deletingId !== null}
          dialogTitle={tr('InternalComments.deleteTitle', 'Delete comment')}
          dialogMessage={tr('InternalComments.deleteMessage', 'Do you really want to delete this comment?')}
          onConfirm={confirmDelete}
          onCancel={() => setDeletingId(null)}
          onAcceptButtonText={tr('InternalComments.delete', 'Delete')}
          onCancelButtonText={tr('InternalComments.cancel', 'Cancel')}
        />
      </>
    </RingilSidebar>
  );
};

export default InternalCommentsSidebar;